import "./EditableMetadocName.scss";

import cx from "classnames";
import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateMetadocMutation } from "services/metadoc-api";
import {
  METADOC_NAME_MAX_LENGTH,
  metadocNameSchema,
} from "components/Documents/table/metadocNameValidation";

type EditableMetadocNameProps = {
  metadocId: number;
  metadocName: string;
  className?: string;
};

const EditableMetadocName = ({
  metadocId,
  metadocName,
  className,
}: EditableMetadocNameProps) => {
  const [updateMetadoc] = useUpdateMetadocMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(metadocName);
  const [isHovered, setIsHovered] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // reset value when metadoc name changes
  useEffect(() => {
    setValue(metadocName);
  }, [metadocName]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const nameValidation = metadocNameSchema.safeParse(value);
    if (!nameValidation.success) {
      KaboodleToaster.error(
        nameValidation.error.issues[0]?.message ?? "Invalid metadoc name",
      );
      setValue(metadocName);
      setIsEditing(false);
      return;
    }
    const trimmedValue = nameValidation.data;

    if (trimmedValue === metadocName) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await updateMetadoc({
        metadocId,
        data: {
          name: trimmedValue,
        },
      }).unwrap();

      setIsEditing(false);
      KaboodleToaster.success("Metadoc name updated successfully");
    } catch (error) {
      console.error("Failed to update metadoc name:", error);
      KaboodleToaster.error("Failed to update metadoc name");
      setValue(metadocName);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [metadocId, metadocName, value, updateMetadoc, isSaving]);

  const handleCancel = useCallback(() => {
    setValue(metadocName);
    setIsEditing(false);
  }, [metadocName]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= METADOC_NAME_MAX_LENGTH) {
        setValue(newValue);
      }
    },
    [],
  );

  // focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const shouldShowTooltip = metadocName.length > 50;

  const nameContent = (
    <div
      className={cx("editable-metadoc-name", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...(shouldShowTooltip && {
        "data-tooltip-id": "metadoc-name-tooltip",
        "data-tooltip-content": metadocName,
        "data-tooltip-place": "top",
      })}
    >
      <span className="material-icons">description</span>
      <span className="editable-metadoc-name__text">{metadocName}</span>
      <button
        className={cx("editable-metadoc-name__edit-button", {
          "editable-metadoc-name__edit-button--visible": isHovered,
        })}
        onClick={handleEdit}
        title="Edit metadoc name"
      >
        <span className="material-icons">edit</span>
      </button>
    </div>
  );

  if (isEditing) {
    return (
      <div
        className={cx(
          "editable-metadoc-name",
          "editable-metadoc-name--editing",
          className,
        )}
        // allow keyboard events to pass through to the input
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <span className="material-icons">description</span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleSave}
          // allow keyboard events to pass through to the input
          onKeyDown={(e) => {
            e.stopPropagation();
            handleKeyDown(e);
          }}
          className="editable-metadoc-name__input"
          disabled={isSaving}
          maxLength={METADOC_NAME_MAX_LENGTH}
        />
        {isSaving && (
          <div className="editable-metadoc-name__saving">
            <span className="material-icons">hourglass_empty</span>
          </div>
        )}
      </div>
    );
  }

  return nameContent;
};

export default EditableMetadocName;
